import {effect, Injectable, signal} from '@angular/core';
import {Subject} from 'rxjs';
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";

export type MenuMode =
  | 'static'
  | 'overlay'
  | 'horizontal'
  | 'slim'
  | 'slim-plus'
  | 'reveal'
  | 'drawer';

export type ColorScheme = 'light' | 'dark';

export interface AppConfig {
  inputStyle: string;
  colorScheme: ColorScheme;
  componentTheme: string;
  ripple: boolean;
  menuMode: MenuMode;
  scale: number;
  menuTheme: string;
  topbarTheme: string;
  menuProfilePosition: string;
}

interface LayoutState {
  staticMenuMobileActive: boolean;
  overlayMenuActive: boolean;
  staticMenuDesktopInactive: boolean;
  configSidebarVisible: boolean;
  menuHoverActive: boolean;
  rightMenuActive: boolean;
  topbarMenuActive: boolean;
  menuProfileActive: boolean;
  sidebarActive: boolean;
  anchored: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  _config: AppConfig = {
    ripple: true,
    inputStyle: 'outlined',
    menuMode: 'slim',
    colorScheme: 'light',
    componentTheme: 'slateblue',
    scale: 14,
    menuTheme: 'sand',
    topbarTheme: 'slateblue',
    menuProfilePosition: 'end',
  };

  state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false,
    rightMenuActive: false,
    topbarMenuActive: false,
    menuProfileActive: false,
    sidebarActive: false,
    anchored: false,
  };

  config = signal<AppConfig>(this._config);

  private configUpdate = new Subject<AppConfig>();
  configUpdate$ = this.configUpdate.asObservable();
  private overlayOpen = new Subject<any>();
  overlayOpen$ = this.overlayOpen.asObservable();
  private topbarMenuOpen = new Subject<any>();
  topbarMenuOpen$ = this.topbarMenuOpen.asObservable();
  private menuProfileOpen = new Subject<any>();
  menuProfileOpen$ = this.menuProfileOpen.asObservable();

  constructor(private router: Router) {
    effect(() => {
      const config = this.config();

      if (this.updateStyle(config)) {
        this.changeTheme();
      }

      this.changeScale(config.scale);
      this.onConfigUpdate();
    });
    // Listen to route changes
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.checkAndUpdateMenuMode());
  }

  updateStyle(config: AppConfig) {
    return (
      config.componentTheme !== this._config.componentTheme ||
      config.colorScheme !== this._config.colorScheme
    );
  }

  changeTheme() {
    let {colorScheme, componentTheme} = this.config();
    const themeLink = <HTMLLinkElement>(
      document.getElementById('theme-link')
    );
    const themeLinkHref = themeLink.getAttribute('href')!;
    const newHref = themeLinkHref
      .split('/')
      .map((el) =>
        el == this._config.componentTheme
          ? (el = componentTheme)
          : el == `theme-${this._config.colorScheme}`
            ? (el = `theme-${colorScheme}`)
            : el
      )
      .join('/');
    this.replaceThemeLink(newHref);
  }

  replaceThemeLink(href: string) {
    const id = 'theme-link';
    let themeLink = <HTMLLinkElement>document.getElementById(id);
    const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

    cloneLinkElement.setAttribute('href', href);
    cloneLinkElement.setAttribute('id', id + '-clone');

    themeLink.parentNode!.insertBefore(
      cloneLinkElement,
      themeLink.nextSibling
    );
    cloneLinkElement.addEventListener('load', () => {
      themeLink.remove();
      cloneLinkElement.setAttribute('id', id);
    });
  }

  changeScale(value: number) {
    document.documentElement.style.fontSize = `${value}px`;
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.state.overlayMenuActive = !this.state.overlayMenuActive;

      if (this.state.overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.state.staticMenuDesktopInactive =
        !this.state.staticMenuDesktopInactive;
    } else {
      this.state.staticMenuMobileActive =
        !this.state.staticMenuMobileActive;

      if (this.state.staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  onTopbarMenuToggle() {
    this.state.topbarMenuActive = !this.state.topbarMenuActive;
    if (this.state.topbarMenuActive) {
      this.topbarMenuOpen.next(null);
    }
  }

  onOverlaySubmenuOpen() {
    this.overlayOpen.next(null);
  }

  showConfigSidebar() {
    this.state.configSidebarVisible = true;
  }

  isOverlay() {
    return this.config().menuMode === 'overlay';
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isSlim() {
    return this.config().menuMode === 'slim';
  }

  isSlimPlus() {
    return this.config().menuMode === 'slim-plus';
  }

  isHorizontal() {
    return this.config().menuMode === 'horizontal';
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = {...this.config()};
    this.configUpdate.next(this.config());
  }

  isRightMenuActive(): boolean {
    return this.state.rightMenuActive;
  }

  openRightSidebar(): void {
    this.state.rightMenuActive = true;
  }

  onMenuProfileToggle() {
    this.state.menuProfileActive = !this.state.menuProfileActive;
    if (
      this.state.menuProfileActive &&
      this.isHorizontal() &&
      this.isDesktop()
    ) {
      this.menuProfileOpen.next(null);
    }
  }

  private checkAndUpdateMenuMode() {
    const currentRoute = this.router.url;
    if (currentRoute.includes('/documentations-portal/internal/view')) {
      this._config.menuMode = 'static';
      this.config.set(this._config);
    } else {
      this._config.menuMode = 'slim';
      this.config.set(this._config);
    }
  }
}
